import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useLanguage } from '../i18n/LanguageContext';

// Smooth slide animation
const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;


// Ultra Premium Dark Header
const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: linear-gradient(90deg, rgba(235, 240, 245, 0.95) 0%, rgba(235, 240, 245, 0.9) 10%, rgba(10, 22, 40, 0.9) 22%, rgba(26, 39, 68, 0.95) 100%);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  z-index: 1000;
  height: 90px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.4s ease;
  
  [dir="rtl"] & {
    background: linear-gradient(-90deg, rgba(235, 240, 245, 0.95) 0%, rgba(235, 240, 245, 0.9) 10%, rgba(10, 22, 40, 0.9) 22%, rgba(26, 39, 68, 0.95) 100%);
  }

  &:hover {
    background: linear-gradient(90deg, rgba(235, 240, 245, 0.98) 0%, rgba(235, 240, 245, 0.95) 10%, rgba(10, 22, 40, 0.95) 22%, rgba(26, 39, 68, 0.98) 100%);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    
    [dir="rtl"] & {
      background: linear-gradient(-90deg, rgba(235, 240, 245, 0.98) 0%, rgba(235, 240, 245, 0.95) 10%, rgba(10, 22, 40, 0.95) 22%, rgba(26, 39, 68, 0.98) 100%);
    }
  }
  
  @media (max-width: 992px) {
    background: linear-gradient(90deg, rgba(235, 240, 245, 0.98) 0%, rgba(235, 240, 245, 0.98) 40%, rgba(10, 22, 40, 0.95) 90%, rgba(26, 39, 68, 0.98) 100%);
    
    [dir="rtl"] & {
      background: linear-gradient(-90deg, rgba(235, 240, 245, 0.98) 0%, rgba(235, 240, 245, 0.98) 40%, rgba(10, 22, 40, 0.95) 90%, rgba(26, 39, 68, 0.98) 100%);
    }

    &:hover {
      background: linear-gradient(90deg, rgba(235, 240, 245, 0.98) 0%, rgba(235, 240, 245, 0.98) 40%, rgba(10, 22, 40, 0.95) 90%, rgba(26, 39, 68, 0.98) 100%);
      
      [dir="rtl"] & {
        background: linear-gradient(-90deg, rgba(235, 240, 245, 0.98) 0%, rgba(235, 240, 245, 0.98) 40%, rgba(10, 22, 40, 0.95) 90%, rgba(26, 39, 68, 0.98) 100%);
      }
    }
  }
`;

const HeaderContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 5%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
`;

const LogoWrapper = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: scale(1.02);
  }
  
  .logo-image {
    height: 65px;
    width: auto;
    transition: all 0.3s ease;
    opacity: 0.95;
  }
  
  &:hover .logo-image {
    opacity: 1;
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  
  @media (max-width: 992px) {
    display: none;
  }
`;

const NavList = styled.ul`
  display: flex;
  align-items: center;
  gap: 40px;
`;

const NavItem = styled.li`
  position: relative;
  height: 90px;
  display: flex;
  align-items: center;
  
  &:hover .submenu {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
    animation: ${slideDown} 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
`;

const NavLink = styled(Link)`
  color: rgba(255, 255, 255, 0.85);
  font-weight: 400;
  font-size: 1.05rem;
  transition: all 0.3s ease;
  letter-spacing: 0.5px;
  position: relative;
  padding: 8px 0;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background: #c9a96e;
    transition: width 0.3s ease;
  }
  
  ${NavItem}:hover & {
    color: #ffffff;
  }
  
  ${NavItem}:hover &::after {
    width: 100%;
  }
`;

const Submenu = styled.ul`
  position: absolute;
  top: 90px;
  left: 50%;
  transform: translateX(-50%) translateY(-10px);
  background: rgba(52, 58, 64, 0.95);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  min-width: 240px;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1201;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  padding: 10px 0;
  
  &::before {
    content: '';
    position: absolute;
    top: -6px;
    left: 50%;
    transform: translateX(-50%) rotate(45deg);
    width: 12px;
    height: 12px;
    background: rgba(52, 58, 64, 0.95);
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    border-left: 1px solid rgba(255, 255, 255, 0.08);
  }
  
  li {
    transition: all 0.2s ease;
  }
  
  a {
    display: block;
    padding: 12px 24px;
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.95rem;
    
    transition: all 0.2s ease;
    
    &:hover {
      color: #c9a96e;
      background: rgba(255, 255, 255, 0.03);
      padding-left: 30px;
    }
  }
`;

// Enhanced Contact Info with Hover Effects
const ContactInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 30px;
  
  @media (max-width: 992px) {
    display: none;
  }
`;

const LangSwitcher = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 4px;
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const LangButton = styled.button`
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: ${props => props.active ? '600' : '400'};
  color: ${props => props.active ? '#c9a96e' : 'rgba(255, 255, 255, 0.6)'};
  background: ${props => props.active ? 'rgba(201, 169, 110, 0.15)' : 'transparent'};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    color: ${props => props.active ? '#c9a96e' : '#ffffff'};
    background: ${props => props.active ? 'rgba(201, 169, 110, 0.15)' : 'rgba(255, 255, 255, 0.1)'};
  }
`;

const PhoneNumber = styled.a`
  color: #ffffff;
  font-weight: 600;
  font-size: 1rem;
  padding: 10px 24px;
  border-radius: 30px;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg, #c9a96e 0%, #b08d55 100%);
  box-shadow: 0 6px 20px rgba(201, 169, 110, 0.25);
  transition: all 0.3s ease;
  text-decoration: none;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(201, 169, 110, 0.35);
    filter: brightness(1.05);
  }
  
  .icon {
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .icon svg {
    width: 100%;
    height: 100%;
    fill: #ffffff;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  padding: 0;
  border-radius: 12px;
  width: 48px;
  height: 48px;
  z-index: 1200;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
  
  @media (max-width: 992px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const MenuIcon = styled.div`
  position: relative;
  width: 22px;
  height: 16px;
`;

const IconElement = styled.span`
  display: block;
  position: absolute;
  width: 100%;
  height: 2px;
  border-radius: 2px;
  background: #ffffff;
  transition: all 0.3s ease;
  
  &:nth-child(1) {
    top: ${props => props.isOpen ? '50%' : '0'};
    transform: ${props => props.isOpen ? 'translateY(-50%) rotate(45deg)' : 'none'};
  }
  
  &:nth-child(2) {
    top: 50%;
    transform: translateY(-50%);
    opacity: ${props => props.isOpen ? '0' : '1'};
  }
  
  &:nth-child(3) {
    bottom: ${props => props.isOpen ? '50%' : '0'};
    transform: ${props => props.isOpen ? 'translateY(50%) rotate(-45deg)' : 'none'};
  }
`;

// ═══ MOBILE MENU: Corporate Full-Screen Overlay ═══

const MobileOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: linear-gradient(165deg, #0a1628 0%, #152238 40%, #1a2744 100%);
  z-index: 1100;
  opacity: ${props => props.open ? 1 : 0};
  visibility: ${props => props.open ? 'visible' : 'hidden'};
  transition: opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1), visibility 0.5s;
  display: flex;
  flex-direction: column;
`;

const OverlayHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px 28px;
  border-bottom: 1px solid rgba(255,255,255,0.08);
  flex-shrink: 0;

  .brand {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .brand-logo {
    height: 42px;
    width: auto;
    opacity: 0.9;
  }

  .close-btn {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: 1px solid rgba(255,255,255,0.15);
    background: rgba(255,255,255,0.05);
    color: rgba(255,255,255,0.7);
    font-size: 1.1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    &:hover {
      background: rgba(255,255,255,0.1);
      border-color: rgba(255,255,255,0.3);
      color: #fff;
    }
  }
`;

const OverlayBody = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 32px 28px;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const OverlayNavItem = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 0;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  text-decoration: none;
  color: rgba(255,255,255,0.85);
  font-size: 1.25rem;
  font-weight: 400;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;

  &:first-child { padding-top: 0; }

  &:hover, &:active {
    color: #ffffff;
    padding-left: 8px;
  }

  .arrow {
    font-size: 0.8rem;
    color: rgba(255,255,255,0.25);
    transition: all 0.3s ease;
  }

  &:hover .arrow {
    color: #c9a96e;
    transform: translateX(4px);
  }
`;

const OverlayAccordionBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 20px 0;
  border: none;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  background: none;
  color: rgba(255,255,255,0.85);
  font-size: 1.25rem;
  font-weight: 400;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: left;

  &:hover { color: #ffffff; padding-left: 8px; }

  .chevron {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 1px solid rgba(255,255,255,0.12);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.65rem;
    color: rgba(255,255,255,0.4);
    transition: all 0.35s ease;
    transform: ${props => props.open ? 'rotate(180deg)' : 'rotate(0)'};
    background: ${props => props.open ? 'rgba(201,169,110,0.15)' : 'transparent'};
    border-color: ${props => props.open ? 'rgba(201,169,110,0.3)' : 'rgba(255,255,255,0.12)'};
    color: ${props => props.open ? '#c9a96e' : 'rgba(255,255,255,0.4)'};
  }
`;

const OverlaySubMenu = styled.div`
  display: grid;
  grid-template-rows: ${props => props.open ? '1fr' : '0fr'};
  transition: grid-template-rows 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border-bottom: ${props => props.open ? '1px solid rgba(255,255,255,0.06)' : 'none'};

  .submenu-inner {
    overflow: hidden;
  }
`;

const OverlaySubLink = styled(Link)`
  display: block;
  padding: 14px 0 14px 24px;
  color: rgba(255,255,255,0.5);
  text-decoration: none;
  font-size: 1rem;
  
  letter-spacing: 0.3px;
  border-left: 2px solid rgba(255,255,255,0.08);
  transition: all 0.25s ease;
  margin-left: 12px;

  &:first-child { padding-top: 16px; }
  &:last-child { padding-bottom: 18px; }

  &:hover, &:active {
    color: #c9a96e;
    border-left-color: #c9a96e;
    padding-left: 32px;
  }
`;

const OverlayFooter = styled.div`
  flex-shrink: 0;
  padding: 24px 28px 32px;
  border-top: 1px solid rgba(255,255,255,0.08);

  .contact-row {
    display: flex;
    gap: 16px;
    margin-bottom: 20px;
  }

  .contact-item {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 16px;
    border-radius: 12px;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.06);
    text-decoration: none;
    color: rgba(255,255,255,0.7);
    font-size: 0.85rem;
    font-weight: 400;
    transition: all 0.25s ease;

    .ci-icon {
      font-size: 1rem;
      opacity: 0.6;
    }

    &:hover {
      background: rgba(255,255,255,0.07);
      border-color: rgba(255,255,255,0.12);
      color: #fff;
    }
  }

  .lang-row {
    display: flex;
    gap: 8px;
  }
`;

const OverlayLangBtn = styled.button`
  flex: 1;
  padding: 12px;
  border-radius: 10px;
  border: 1px solid ${props => props.active ? 'rgba(201,169,110,0.4)' : 'rgba(255,255,255,0.08)'};
  background: ${props => props.active ? 'rgba(201,169,110,0.12)' : 'rgba(255,255,255,0.03)'};
  color: ${props => props.active ? '#c9a96e' : 'rgba(255,255,255,0.4)'};
  font-weight: ${props => props.active ? '700' : '400'};
  font-size: 0.85rem;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    border-color: rgba(201,169,110,0.3);
    color: ${props => props.active ? '#c9a96e' : 'rgba(255,255,255,0.7)'};
  }
`;

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState(false);
  const { t, language, setLanguage } = useLanguage();

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    setMobileSubmenuOpen(false);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileSubmenuOpen(false);
  };

  return (
    <>
    <HeaderContainer>
      <HeaderContent>
        <LogoWrapper to="/">
          <img src="/images/DMR cimento (1)_page-0001.png" alt="DMR Çimento" className="logo-image" />
        </LogoWrapper>
        <Nav>
          <NavList>
            <NavItem><NavLink to="/" className="nav-link">{t('header.home')}</NavLink></NavItem>
            <NavItem>
              <NavLink to="/hakkimizda" className="nav-link">{t('header.corporate')}</NavLink>
              <Submenu className="submenu">
                <li><Link to="/hakkimizda">{t('header.about')}</Link></li>
                <li><Link to="/hakkimizda/insan-kaynaklari">{t('header.hr')}</Link></li>
                <li><Link to="/hakkimizda/gizlilik">{t('header.privacy')}</Link></li>
                <li><Link to="/hakkimizda/sartlar">{t('header.terms')}</Link></li>
                <li><Link to="/hakkimizda/sss">{t('header.faq')}</Link></li>
                <li><Link to="/hakkimizda/vizyon">{t('header.vision')}</Link></li>
                <li><Link to="/hakkimizda/kalite">{t('header.quality')}</Link></li>
                <li><Link to="/hakkimizda/degerler">{t('header.values')}</Link></li>
              </Submenu>
            </NavItem>
            <NavItem>
              <NavLink to="/fabrikalar" className="nav-link">{t("header.factories")}</NavLink>
              <Submenu className="submenu">
                <li><Link to="/fabrikalar/urfa">{t("menu.urfa")}</Link></li>
                <li><Link to="/fabrikalar/mardin">{t("menu.mardin")}</Link></li>
                <li><Link to="/fabrikalar/nigde">{t("menu.nigde")}</Link></li>
                <li><Link to="/fabrikalar/halep">{t("menu.halep")}</Link></li>
              </Submenu>
            </NavItem>
            <NavItem>
              <NavLink to="/santraller" className="nav-link">{t("header.plants")}</NavLink>
              <Submenu className="submenu">
                <li><Link to="/santraller/urfa-beton">{t("menu.urfa")}</Link></li>
                <li><Link to="/santraller/hatay-beton">{t("menu.hatay")}</Link></li>
                <li><Link to="/santraller/kilis-beton">{t("menu.kilis")}</Link></li>
                <li><Link to="/santraller/rakka-beton">{t("menu.rakka")}</Link></li>
              </Submenu>
            </NavItem>
            <NavItem>
              <NavLink to="/istirakler" className="nav-link">{t("header.subsidiaries")}</NavLink>
              <Submenu className="submenu">
                <li><Link to="/istirakler/lojistik">{t("menu.lojistik")}</Link></li>
                <li><Link to="/istirakler/rakka-alci">{t("menu.rakkaAlci")}</Link></li>
                <li><Link to="/istirakler/urfa-kalker">{t("menu.urfaKalker")}</Link></li>
              </Submenu>
            </NavItem>
            <NavItem><NavLink to="/iletisim" className="nav-link">{t('header.contact')}</NavLink></NavItem>
          </NavList>
        </Nav>
        <ContactInfo>
          <LangSwitcher>
            <LangButton active={language === 'tr'} onClick={() => setLanguage('tr')}>TR</LangButton>
            <LangButton active={language === 'en'} onClick={() => setLanguage('en')}>EN</LangButton>
            <LangButton active={language === 'ar'} onClick={() => setLanguage('ar')}>AR</LangButton>
          </LangSwitcher>
          <PhoneNumber href="tel:+905321316330">
            <span className="icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.02-.24c1.12.37 2.33.57 3.57.57a1 1 0 011 1V21a1 1 0 01-1 1C10.3 22 2 13.7 2 3a1 1 0 011-1h3.5a1 1 0 011 1c0 1.24.2 2.45.57 3.57a1 1 0 01-.25 1.02l-2.2 2.2z"/>
              </svg>
            </span>
            <bdi>0532 131 63 30</bdi>
          </PhoneNumber>
        </ContactInfo>
        <MobileMenuButton onClick={toggleMobileMenu} isOpen={mobileMenuOpen}>
          <MenuIcon isOpen={mobileMenuOpen}>
            <IconElement isOpen={mobileMenuOpen} />
            <IconElement isOpen={mobileMenuOpen} />
            <IconElement isOpen={mobileMenuOpen} />
          </MenuIcon>
        </MobileMenuButton>
      </HeaderContent>
    </HeaderContainer>

    <MobileOverlay open={mobileMenuOpen}>
      <OverlayHeader>
        <div className="brand">
          <img src="/images/DMR cimento (1)_page-0001.png" alt="" className="brand-logo" />
        </div>
        <button className="close-btn" onClick={closeMobileMenu}>✕</button>
      </OverlayHeader>

      <OverlayBody>
        <OverlayNavItem to="/" onClick={closeMobileMenu}>
          {t('header.home')}
          <span className="arrow">→</span>
        </OverlayNavItem>

        <OverlayNavItem to="/fabrikalar" onClick={closeMobileMenu}>{t("header.factories")}<span className="arrow">→</span>
        </OverlayNavItem>
        <OverlayNavItem to="/santraller" onClick={closeMobileMenu}>{t("header.plants")}<span className="arrow">→</span>
        </OverlayNavItem>
        <OverlayNavItem to="/istirakler" onClick={closeMobileMenu}>{t("header.subsidiaries")}<span className="arrow">→</span>
        </OverlayNavItem>

        <OverlayAccordionBtn open={mobileSubmenuOpen} onClick={() => setMobileSubmenuOpen(!mobileSubmenuOpen)}>
          {t('header.corporate')}
          <span className="chevron">▼</span>
        </OverlayAccordionBtn>
        <OverlaySubMenu open={mobileSubmenuOpen}>
          <div className="submenu-inner">
            <OverlaySubLink to="/hakkimizda" onClick={closeMobileMenu}>{t('header.about')}</OverlaySubLink>
            <OverlaySubLink to="/hakkimizda/vizyon" onClick={closeMobileMenu}>{t('header.vision')}</OverlaySubLink>
            <OverlaySubLink to="/hakkimizda/degerler" onClick={closeMobileMenu}>{t('header.values')}</OverlaySubLink>
            <OverlaySubLink to="/hakkimizda/kalite" onClick={closeMobileMenu}>{t('header.quality')}</OverlaySubLink>
            <OverlaySubLink to="/hakkimizda/insan-kaynaklari" onClick={closeMobileMenu}>{t('header.hr')}</OverlaySubLink>
            <OverlaySubLink to="/hakkimizda/sartlar" onClick={closeMobileMenu}>{t('header.terms')}</OverlaySubLink>
            <OverlaySubLink to="/hakkimizda/gizlilik" onClick={closeMobileMenu}>{t('header.privacy')}</OverlaySubLink>
            <OverlaySubLink to="/hakkimizda/sss" onClick={closeMobileMenu}>{t('header.faq')}</OverlaySubLink>
          </div>
        </OverlaySubMenu>

        <OverlayNavItem to="/iletisim" onClick={closeMobileMenu}>
          {t('header.contact')}
          <span className="arrow">→</span>
        </OverlayNavItem>
      </OverlayBody>

      <OverlayFooter>
        <div className="contact-row">
          <a href="tel:+905321316330" className="contact-item">
            <span className="ci-icon">📞</span>
            <bdi>0532 131 63 30</bdi>
          </a>
          <a href="mailto:info@dmrcimento.com" className="contact-item">
            <span className="ci-icon">✉️</span>
            info@dmrcimento.com
          </a>
        </div>
        <div className="lang-row">
          <OverlayLangBtn active={language === 'tr'} onClick={() => setLanguage('tr')}>TR</OverlayLangBtn>
          <OverlayLangBtn active={language === 'en'} onClick={() => setLanguage('en')}>EN</OverlayLangBtn>
          <OverlayLangBtn active={language === 'ar'} onClick={() => setLanguage('ar')}>AR</OverlayLangBtn>
        </div>
      </OverlayFooter>
    </MobileOverlay>
    </>
  );
};

export default Header;
